import CardTemplate from "components/card/card-template/CardTemplate";
import { DateOfBirthField, StateField, ZipCodeField } from "components/form-fields";
import type { MLTAApplicationFormReturn } from "types/Card";

type MLTAQuoteStartProps = {
    form: MLTAApplicationFormReturn;

    // Form field values (controlled)
    dobMonth?: string;
    dobDay?: string;
    dobYear?: string;
    stateValue?: string;
    // Computed state
    isUnavailableState: boolean;

    // Event handlers
    onSubmit: () => Promise<void>;
    onDateOfBirthBlur?: () => void;
    isLoading?: boolean;
    firstName?: string;
};

const MLTAQuoteStart = ({
    form,
    dobMonth,
    dobDay,
    dobYear,
    stateValue,
    isUnavailableState,
    onSubmit,
    onDateOfBirthBlur,
    isLoading = false,
    firstName,
}: MLTAQuoteStartProps) => {
    const {
        register,
        formState: { errors },
    } = form;

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        await onSubmit();
    };

    const title = (
        <div className="flex flex-col gap-2 min-h-[76px] items-center justify-center">
            <p className="text-base leading-5 tracking-sm text-base-content pr-2 font-medium text-center">
                Member Loyalty<sup>®</sup>
            </p>
            <p className="text-2xl leading-5 tracking-sm text-secondary-content pr-2 font-semibold text-center">
                Accident Insurance
            </p>
        </div>
    );

    return (
        <CardTemplate title={title} className="text-base-content min-h-[308px] tablet:min-h-[308px]">
            <p className="text-lg leading-5 tracking-sm text-base-content pr-2 font-bold text-center mb-4">
                {firstName ? `Welcome ${firstName}` : "Get a quote"}
            </p>
            <form onSubmit={handleSubmit} data-testid="mlta-quote-start-form">
                <div
                    data-testid="mlta-quote-start-content"
                    className="font-medium text-base-content text-base flex flex-col gap-4"
                >
                    <DateOfBirthField
                        monthRegister={register("quote.dateOfBirth.month")}
                        dayRegister={register("quote.dateOfBirth.day")}
                        yearRegister={register("quote.dateOfBirth.year")}
                        monthValue={dobMonth}
                        dayValue={dobDay}
                        yearValue={dobYear}
                        monthError={errors.quote?.dateOfBirth?.month?.message}
                        dayError={errors.quote?.dateOfBirth?.day?.message}
                        yearError={errors.quote?.dateOfBirth?.year?.message}
                        disabled={isLoading}
                        id="mltaQuoteStart-dob"
                        onBlur={onDateOfBirthBlur}
                    />
                    <div className="flex gap-3">
                        <div className="flex-[2] min-w-0">
                            <StateField
                                register={register("quote.state")}
                                value={stateValue || undefined}
                                error={
                                    isUnavailableState ? "Product not available in your location" : errors.quote?.state?.message
                                }
                                fullWidth={true}
                                disabled={isLoading}
                                id="mltaQuoteStart-state"
                            />
                        </div>

                        <div className="flex-1 min-w-0">
                            <ZipCodeField
                                register={register("quote.zipCode")}
                                error={errors.quote?.zipCode?.message}
                                id="mltaQuoteStart-zipCode"
                            />
                        </div>
                    </div>
                    {isUnavailableState ? (
                        <div className="flex justify-center card bg-warning p-4 gap-2">
                            <p className="text-warning-content font-medium text-base leading-5">
                                We're sorry, but the product you have selected is not available in your area at this time.
                            </p>
                            <p className="text-warning-content font-medium text-sm leading-5">
                                Please ensure that the state you entered is correct, or contact us at{" "}
                                <a className="font-bold" href="tel:8884227020">
                                    (888) 422-7020
                                </a>{" "}
                                to learn more about the life insurance options that may be available to you.
                            </p>
                        </div>
                    ) : (
                        <div className="flex justify-center w-full">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="btn bg-secondary-content font-semibold flex text-sm text-secondary border-none rounded hover:bg-secondary-content/80 min-h-0 h-[36px] px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                                {isLoading && (
                                    <span data-testid="loading-spinner" className="loading loading-spinner loading-sm" />
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </form>
        </CardTemplate>
    );
};

export default MLTAQuoteStart;
